import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Server, Terminal as TerminalIcon, X, Search, ChevronDown } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { dropdownSide } from '../../utils/dropdownSide';
import { useTerminalExtStore, tabKind, tabPanes } from '../../store/terminalExtStore';

const tabExited = (st, tab) => {
  const ps = tabPanes(st, tab);
  return ps.length > 0 && ps.every((p) => p.status === 'exited' || p.status === 'error');
};

/**
 * "All tabs" — the chevron after "+", with a search, as in every other tab
 * strip. Moved out of TerminalExtView; a tab's icon and "exited" note now
 * come from its panes.
 */
export default function TerminalAllTabsMenu({ tabs, activeTabId, onSelect, onClose }) {
  const { t } = useTranslation();
  const panes = useTerminalExtStore((s) => s.panes);
  // "All tabs" dropdown (same idiom as the Collections tab bar's left button).
  const [allTabsOpen, setAllTabsOpen] = useState(false);
  const [tabQuery, setTabQuery] = useState('');
  const allTabsRef = useRef(null);
  useEffect(() => {
    if (!allTabsOpen) return undefined;
    const onDown = (e) => {
      if (allTabsRef.current && !allTabsRef.current.contains(e.target)) {
        setAllTabsOpen(false);
        setTabQuery('');
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [allTabsOpen]);
  const tq = tabQuery.trim().toLowerCase();
  const dropdownTabs = tq ? tabs.filter((tab) => tab.label.toLowerCase().includes(tq)) : tabs;

  return (
    <div className="relative flex-shrink-0" ref={allTabsRef}>
      <Tooltip content={`${t('tabs.allTabs', 'All tabs')} (${tabs.length})`} side="bottom">
        <button
          type="button"
          className="flex items-center space-x-1 px-2 py-1 flex-shrink-0 cursor-pointer transition-colors duration-150 etm-tab etm-tab--inactive"
          onClick={() => setAllTabsOpen((v) => !v)}
          aria-expanded={allTabsOpen}
          aria-label={t('tabs.allTabs', 'All tabs')}
        >
          <ChevronDown size={14} className="flex-shrink-0" />
          {/* invisible spacer — same height as a tab with a close button */}
          <span
            aria-hidden="true"
            style={{
              width: 0,
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 0',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <X size={11} />
          </span>
        </button>
      </Tooltip>

      {allTabsOpen && (
        <div
          className="absolute w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 flex flex-col overflow-hidden dark:border-zinc-700 dark:bg-zinc-900"
          // explicit: `top-full` / `right-0` are not in this project's utility subset
          style={{
            top: '100%',
            marginTop: 8,
            maxHeight: 'min(420px, calc(100vh - 100px))',
            ...(() => {
              const side = dropdownSide(allTabsRef.current?.getBoundingClientRect(), 288);
              return 'left' in side ? { left: 0 } : { right: 0 };
            })(),
          }}
        >
          {/* No title row: the search is the header, as in the shared tab strip. */}
          <div className="w-full flex-shrink-0 border-b border-gray-100 px-3 py-2">
            <div className="flex w-full min-w-0 items-center gap-2">
              <Search size={14} className="text-gray-400 flex-shrink-0" aria-hidden />
              <input
                autoFocus
                type="text"
                value={tabQuery}
                onChange={(e) => setTabQuery(e.target.value)}
                placeholder={t('terminalExt.searchTabs', 'Search terminals')}
                className="tab-dropdown-search-input min-w-0 w-full flex-1 bg-transparent text-gray-900 placeholder-gray-400 border-none p-0 focus:outline-none"
                autoComplete="off"
                aria-label={t('terminalExt.searchTabs', 'Search terminals')}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    setAllTabsOpen(false);
                    setTabQuery('');
                  }
                }}
              />
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center">
                {tabQuery ? (
                  <button
                    type="button"
                    className="flex h-full w-full items-center justify-center rounded bg-transparent p-0 cursor-pointer text-gray-900 hover:opacity-70 focus:outline-none"
                    aria-label={t('sidebar.clearSearch', 'Clear filter')}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      setTabQuery('');
                    }}
                  >
                    <X size={14} strokeWidth={2} aria-hidden />
                  </button>
                ) : null}
              </div>
            </div>
          </div>
          <div className="panel-scroll flex-1 min-h-0 py-2" style={{ overflowX: 'hidden' }}>
            {dropdownTabs.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-gray-500">
                {t('terminalExt.noTabMatches', 'No terminals match.')}
              </div>
            ) : (
              dropdownTabs.map((tab) => (
                <div
                  key={tab.id}
                  onClick={() => {
                    onSelect(tab.id);
                    setAllTabsOpen(false);
                    setTabQuery('');
                  }}
                  className={`px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-1 min-w-0 group ${
                    tab.id === activeTabId ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                  }`}
                >
                  <span className="flex-shrink-0 w-12 flex items-center">
                    {tabKind({ panes }, tab) === 'ssh' ? (
                      <Server size={14} className="text-emerald-600" aria-label="SSH" />
                    ) : (
                      <TerminalIcon size={14} className="text-gray-500" aria-label="Terminal" />
                    )}
                  </span>
                  <span className="text-xs truncate min-w-0 flex-1">{tab.label}</span>
                  {tabExited({ panes }, tab) && (
                    <span className="text-xxs text-gray-400 flex-shrink-0">{t('terminalExt.exited', 'exited')}</span>
                  )}
                  <button
                    type="button"
                    className="tab-bar-close-btn flex-shrink-0 cursor-pointer rounded-lg bg-transparent py-1 transition-colors opacity-0 group-hover:opacity-100"
                    aria-label={t('common.close')}
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose(tab.id);
                    }}
                  >
                    <X size={11} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
